<template>
<div id="drawerMask"></div>
<div id="drawer" :class="{ show: showing }" class="d-flex flex-column">
    <div id="closeWrapper" class="d-flex align-items-center">
        <h3 class="flex-grow-1">{{ title }}</h3>
        <button 
            id="close" 
            @click="hide()"
            aria-label="Close"
        >
            <font-awesome-icon icon="x" size="lg" class="bookingItemIcon"></font-awesome-icon>
        </button>
    </div>
    <div id="drawerContentWrapper">
        <slot></slot>
    </div>
</div>
<div id="drawerBgOverlay" :class="{ show: showing }"></div>
</template>

<script setup>
import {ref, onMounted, defineExpose, defineProps} from 'vue'

const showing = ref(false)

onMounted(
    () => {
        setParentRelative()
    }
)

//Allow other components to call these functions
defineExpose(
    {
        show,
        hide
    }
)

const props = defineProps({
    title: {
        type: String,
        default: ''
    }
})

/**
 *  Set the parent of the #drawer element's position to relative so the drawer is positioned correctly
 *  For the current app setup this means the  body > #app > #contain element
 *  Thought this was the best approach now rather than change the positon element globally because it's used on every app page so may have unintended consequences. 
 * 
 * @returns {void}
 */
function setParentRelative() {
    const drawerParent = document.getElementById('drawer').parentElement
    if(drawerParent) {
        drawerParent.style.position = 'relative';
    }
}

/**
 *  Set the showing value to true so the 'show' class will be applied to elements to make drawer show
 *  Stop body element from being scrollable with overflow: hidden
 * 
 * @returns {void}
 */
function show() {
    showing.value = true;
    const body = document.getElementsByTagName('body')
    body[0].style.overflow = 'hidden'
}

/**
 *  Set the showing value to false so the 'show' class will be removed to elements to hide drawer
 *  Remove overflow:hidden from body so it can be scrolled
 * 
 * @returns {void}
 */
function hide() {
    showing.value = false;
    const body = document.getElementsByTagName('body')
    body[0].style.overflow = ''
}

</script>

<style>

:root {
  --top-nav-height: 74px;
}

#drawerMask {
    position: absolute;
    top: var(--top-nav-height);
    bottom: 0;
    width: 80%;
    transform: translateX(-100%);
    background: #fff;
    z-index: 999;
}

#drawer {
    position: absolute;
    top: var(--top-nav-height);
    /* Fallback if svh not supported */
    height: calc(100vh - var(--top-nav-height) - 60px - env(safe-area-inset-bottom, 0));
    overflow-y: auto;
    bottom: 0;
    width: 80%;
    max-width: 500px;
    background: #fff;
    z-index: 998;
    transform: translateX(-100%);
    visibility: hidden;
    transition: all 0.2s;
}

@media(max-width:500px) {
   #drawer { 
    width: 100%;
   }
}

@supports(height:100svh) {
    #drawer{
        /* svh to accommodate mobile browser UI */
        height:calc(100svh - var(--top-nav-height) - 60px - env(safe-area-inset-bottom,0))
    }
}

#drawer.show {
    transform: translateX(0%);
    visibility: visible;
}

#drawerBgOverlay.show {
    position: absolute;
    top: var(--top-nav-height);
    bottom: 0;
    width: 100%;
    background: rgba(32, 32, 32, 0.8);
    z-index: 997;
}

#closeWrapper {
    padding: 0.5rem;
    border-bottom: solid 1px #ccc;
    margin-bottom: 10px;
}

#drawer h3 {
    font-size: 24px;
    margin-bottom: 0px;
}

#drawer #close {
    background: transparent;
    border: none;
    height: 44px;
    width: 44px;
}

#drawer #close:hover {
    background: var(--uog-blue-10);
}

#drawer #close svg {
    color: var(--uog-dark-blue);
}

#drawerContentWrapper {
    /* change this because this is specific to features */
    height: calc(100% - 76px);
    overflow-y: auto;
    padding-bottom: 70px;
}

</style>